import { ConditionService } from "@lib/strapi/services/content-types/condition.service";
import { PostCategoryService } from "@lib/strapi/services/content-types/postCategory.service";
import { StrapiPages } from "@lib/strapi/pages";
import {
  StrapiPostDTO,
  PostDTO,
} from "@lib/strapi/dtos/content-types/post.dto";
import { StrapiResponse } from "@lib/strapi/dtos/core/response.dto";
import { StrapiRoutes } from "@lib/strapi/routes";
import { TeamMemberService } from "@lib/strapi/services/content-types/teamMember.service";
import { fetchAPI } from "@lib/strapi/fetch-api";
import { getImageProps, ImageProps } from "@lib/image";
import { parseStrapiContent } from "@lib/strapi/content";

export class PostService {
  static async fromStrapi(
    dto: PostDTO,
    locale: string,
    strapiData?: StrapiPostDTO,
  ): Promise<PostDTO> {
    if (!strapiData) {
      return null;
    }
    if (dto == null) {
      dto = new PostDTO();
    }
    dto.title = strapiData.title;
    dto.slug = strapiData.slug;
    dto.displayDate = strapiData.displayDate;
    dto.teaser = strapiData.teaser;
    dto.content = await parseStrapiContent(strapiData.content);
    dto.featuredImage = getImageProps(strapiData.featuredImage);
    dto.images = await Promise.all(
      (strapiData.images ?? []).map((item) => getImageProps(item)),
    );
    dto.author = await TeamMemberService.fromStrapi(
      null,
      locale,
      strapiData.author,
    );
    dto.categories = await Promise.all(
      (strapiData.categories ?? []).map(
        async (item) =>
          await PostCategoryService.fromStrapi(null, locale, item),
      ),
    );
    dto.conditions = await Promise.all(
      (strapiData.conditions ?? []).map(
        async (item) => await ConditionService.fromStrapi(null, locale, item),
      ),
    );
    dto.seo = strapiData.seo;
    return dto;
  }

  static async fetchAll(authToken: string, locale: string): Promise<PostDTO[]> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = {
      locale,
      sort: ["displayDate:desc"],
      pagination: { pageSize: 500 },
    };
    const response: StrapiResponse<StrapiPostDTO[]> = await fetchAPI(
      StrapiRoutes.posts(),
      urlParamsObject,
      token,
    );
    const dto = new PostDTO();
    return await Promise.all(
      (response.data ?? []).map(
        async (item) =>
          await PostService.fromStrapi(new PostDTO(), locale, item),
      ),
    );
  }

  static async fetchBySlug(
    authToken: string,
    locale: string,
    slug?: string,
  ): Promise<PostDTO> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = { locale, filters: { slug: slug } };
    const response: StrapiResponse<StrapiPostDTO[]> = await fetchAPI(
      StrapiRoutes.posts(),
      urlParamsObject,
      token,
    );
    const dto = new PostDTO();
    if ((response.data ?? []).length === 0) {
      return null;
    }
    return await PostService.fromStrapi(dto, locale, (response.data ?? [])[0]);
  }

  static async fetchLatest(
    authToken: string,
    locale: string,
  ): Promise<PostDTO[]> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = {
      locale,
      filters: { displayDate: { $notNull: true } },
      sort: ["displayDate:desc"],
      pagination: { pageSize: 3 },
    };
    const response: StrapiResponse<StrapiPostDTO[]> = await fetchAPI(
      StrapiRoutes.posts(),
      urlParamsObject,
      token,
    );
    const dto = new PostDTO();
    return await Promise.all(
      (response.data ?? []).map(
        async (item) =>
          await PostService.fromStrapi(new PostDTO(), locale, item),
      ),
    );
  }
}
